import * as msal from '@azure/msal-browser'
import * as Sentry from '@sentry/browser'
import config from '@/config'

const MICROSOFT_LOGIN_REDIRECT_PATHNAME = '/access/microsoft'

const SCOPES = {
  OPENID: 'openid',
  PROFILE: 'profile',
  USER_READ: 'User.Read',
  EMAIL: 'email',
  OFFLINE_ACCESS: 'offline_access',
  USER_IMPERSONATION: 'https://storage.azure.com/user_impersonation',
}

const ERROR_CODES_REQUIRING_INTERACTION = [
  'consent_required',
  'interaction_required',
  'login_required',
]

const DEFAULT_MSAL_CONFIG = {
  auth: {
    clientId: config.azureAppId,
    authority: 'https://login.microsoftonline.com/organizations',
    navigateToLoginRequestUrl: false,
    redirectUri: encodeURI(
      `${document.location.origin}${MICROSOFT_LOGIN_REDIRECT_PATHNAME}`
    ),
  },
  cache: {
    cacheLocation: 'sessionStorage',
    temporaryCacheLocation: 'sessionStorage',
    storeAuthStateInCookie: true,
    secureCookies: true,
  },
  graph: {
    graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
  },
}

const DEFAULT_AUTHORIZATION_ENDPOINT_PATH = '/oauth2/v2.0/authorize'

const msalInstance = new msal.PublicClientApplication(DEFAULT_MSAL_CONFIG)

msalInstance
  .handleRedirectPromise()
  .then(tokenResponse => {
    try {
      if (tokenResponse) {
        redirectAfterLogin(tokenResponse)
      } else if (location.pathname === MICROSOFT_LOGIN_REDIRECT_PATHNAME) {
        loginRedirect()
      }
    } catch (error) {
      handleAuthError(error)
    }
  })
  .catch(error => {
    handleAuthError(error)
  })

function redirectAfterLogin(tokenResponse) {
  window?.$router?.replace?.({
    name: 'MicrosoftRedirectView',
    query: {
      accessToken: tokenResponse.accessToken,
      expiresOn: tokenResponse.expiresOn?.getTime?.(),
    },
  })
}

function handleAuthError(error) {
  console.error(error)

  Sentry.captureException(`Azure Auth error: ${error}`)

  window?.$router?.replace?.({
    name: 'MicrosoftRedirectView',
    query: { error: error?.errorMessage ?? error },
  })
}

export async function login(email = null) {
  await msalInstance.loginPopup(
    getLoginRequestObject({ email, forcePopup: true })
  )

  return getToken(getLoginRequestObject())
}

export function loginRedirect(args = {}) {
  const { email, authorityURL } = args

  const instance = authorityURL
    ? getMsalInstanceWithAuthorityURL(authorityURL)
    : msalInstance

  instance.loginRedirect(getLoginRequestObject({ email, forcePopup: true }))
}

function getMsalInstanceWithAuthorityURL(authorityURL) {
  return new msal.PublicClientApplication({
    ...DEFAULT_MSAL_CONFIG,
    auth: {
      ...DEFAULT_MSAL_CONFIG.auth,
      authority: authorityURL.replace(DEFAULT_AUTHORIZATION_ENDPOINT_PATH, ''), // The msal module adds the "authorize" endpoint path to the authority URL automatically. To prevent errors when building the final auth endpoint we have to remove it before creating the new instance.,
    },
  })
}

export function silentlyRefreshSession() {
  return getToken(getLoginRequestObject())
}

export function requestStorageAuth() {
  return getToken({
    scopes: [SCOPES.USER_IMPERSONATION],
  })
}

export function logout() {
  return msalInstance.logout()
}

function getLoginRequestObject(args = {}) {
  const { email, forcePopup } = args

  const requestObject = {
    scopes: [
      SCOPES.OPENID,
      SCOPES.PROFILE,
      SCOPES.USER_READ,
      SCOPES.EMAIL,
      SCOPES.OFFLINE_ACCESS,
    ],
  }

  requestObject.prompt = forcePopup ? 'login' : 'none'
  requestObject.loginHint = email || null

  return requestObject
}

async function getToken(requestObject) {
  try {
    const tokenResponse = await msalInstance.acquireTokenSilent(requestObject)
    return tokenResponse
  } catch (error) {
    Sentry.captureException(
      `Azure - Error while requesting token silently: ${error}`
    )

    if (requiresInteraction(error.errorCode)) {
      return getTokenWithUserInteraction(requestObject)
    }

    throw error
  }
}

function getTokenWithUserInteraction(requestObject) {
  try {
    return msalInstance.acquireTokenRedirect({
      ...requestObject,
      prompt: 'consent',
    })
  } catch (error) {
    Sentry.captureException(
      `Azure - Error while requesting token with user interaction: ${error}`
    )
    throw error
  }
}

function requiresInteraction(errorCode) {
  return ERROR_CODES_REQUIRING_INTERACTION.includes(errorCode)
}
